/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Component for Search Component For Existing Eligibility Request
 */
import _ from 'lodash';
import { useMemo } from 'react';
import { JsonTree } from '@react-awesome-query-builder/antd';
import { IFieldsData, QUERY_BUILDER_FILTER_TYPE } from 'lib/SearchQueryBuilder/config/entity.config';
import { OPERATORS } from 'lib/SearchQueryBuilder/config/queryConfig';

import AdvancedFilters from 'components/advancedFilters';
import useMetaData from 'hooks/useMetadata';
import useKeycloakUsers from 'hooks/admin/useKeycloakUsers';
import { useEligibilityStoreActions, useEligibilityStoreValues } from 'store/eligibilityRequests';
import { useBatchUploadStoreActions, useBatchUploadStoreValues } from 'store/batchUpload';
import { ELIGIBILITY_REQUESTS_TABS } from 'utils/router';
import { getFilterQueryFromSearchParams } from 'utils/advancedFilterUtils';
import { dataKeys } from 'utils/eligibility/eligibilityRequests';
import {
	ADDITIONAL_PAYER_SELECT_OPTIONS,
	ELIGIBILITY_BATCH_LIST_VIEW_FIELDS,
	ELIGIBILITY_BATCH_REQUEST_STATUS_OPTIONS,
	ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES,
	ELIGIBILITY_LIST_VIEW_FIELDS,
	ELIGIBILITY_STATUS_MULTI_SELECT_OPTIONS,
	INSURANCE_TYPE_MULTI_SELECT_OPTIONS,
} from 'app-constants/eligibility/index';

interface IProps {
	activeTab: string;
}

const filterDataKeys = {
	batchID: 'batchID',
	status: 'status',
	createdBy: 'createdBy',
	creationDate: 'creationDate',
};

const SERVICE_DATE_FILTER_SCHEMA = {
	ui_field_type: QUERY_BUILDER_FILTER_TYPE.rangePicker,
	searchOperator: OPERATORS.equal,
	isSearchable: true,
	operators: [OPERATORS.between],
	field: dataKeys.dateOfService,
	display_name: ELIGIBILITY_LIST_VIEW_FIELDS.dateOfService.label,
	custom_props: {
		placeholder: ELIGIBILITY_LIST_VIEW_FIELDS.dateOfService.placeholder,
	},
};

const ALL_TAB_STATUS_FILTER_SCHEMA = {
	ui_field_type: QUERY_BUILDER_FILTER_TYPE.multiselect,
	searchOperator: OPERATORS.multiselectContains,
	isSearchable: true,
	field: dataKeys.coverageStatus,
	options: ELIGIBILITY_STATUS_MULTI_SELECT_OPTIONS,
	display_name: ELIGIBILITY_LIST_VIEW_FIELDS.eligibilityStatus.label,
	custom_props: {
		placeholder: ELIGIBILITY_LIST_VIEW_FIELDS.eligibilityStatus.placeholder,
	},
};

const BATCH_STATUS_FILTER_SCHEMA = {
	ui_field_type: QUERY_BUILDER_FILTER_TYPE.multiselect,
	searchOperator: OPERATORS.multiselectContains,
	isSearchable: true,
	field: filterDataKeys.status,
	options: ELIGIBILITY_BATCH_REQUEST_STATUS_OPTIONS,
	display_name: ELIGIBILITY_BATCH_LIST_VIEW_FIELDS.status.label,
	custom_props: {
		placeholder: ELIGIBILITY_BATCH_LIST_VIEW_FIELDS.status.placeholder,
	},
};

const getFilterParamsWithServiceDate = (dateRange: [string, string]): JsonTree => {
	return getFilterQueryFromSearchParams({
		searchParams: { key: dataKeys.dateOfService, value: dateRange },
		// TODO: payload still not fixed
		filterSchema: [{ ...SERVICE_DATE_FILTER_SCHEMA, searchOperator: OPERATORS.between }],
	});
};

const getStatusQueryParams = (status: string[]): JsonTree => {
	return getFilterQueryFromSearchParams({
		searchParams: { key: dataKeys.coverageStatus, value: status || [] },
		// TODO: validate payload
		filterSchema: [{ ...ALL_TAB_STATUS_FILTER_SCHEMA }],
	});
};

const IN_PROGRESS_QUERY_PARAMS: {
	filterParams: EligibilityBatchUpload.IBatchEligibilityHistoryQueryModel;
	pageNumber: number;
	pageSize: number;
} = {
	pageNumber: 1,
	pageSize: 1,
	filterParams: {
		criteria: getFilterQueryFromSearchParams({
			searchParams: {
				key: 'status',
				value: [ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.inProgress, ELIGIBILITY_BATCH_UPLOAD_STATUS_CODES.scheduled] as AnyType[],
			},
			filterSchema: [BATCH_STATUS_FILTER_SCHEMA],
		}),
	},
};

const getAllTabFilters = (metadata: Metadata.IResponse): IFieldsData[] => [
	{
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.text,
		searchOperator: OPERATORS.like,
		isSearchable: true,
		field: dataKeys.eligibilityId,
		display_name: ELIGIBILITY_LIST_VIEW_FIELDS.eligibilityId.label,
		custom_props: {
			placeholder: ELIGIBILITY_LIST_VIEW_FIELDS.eligibilityId.placeholder,
		},
	},
	// patientName
	{
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.text,
		searchOperator: OPERATORS.like,
		isSearchable: true,
		isDefaultSelectedField: true,
		field: dataKeys.patientName,
		display_name: ELIGIBILITY_LIST_VIEW_FIELDS.patientName.label,
		custom_props: {
			placeholder: ELIGIBILITY_LIST_VIEW_FIELDS.patientName.placeholder,
		},
	},
	// patientIdentifier
	{
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.text,
		searchOperator: OPERATORS.like,
		isSearchable: true,
		field: dataKeys.patientIdentifier,
		display_name: ELIGIBILITY_LIST_VIEW_FIELDS.identifier.label,
		custom_props: {
			placeholder: ELIGIBILITY_LIST_VIEW_FIELDS.identifier.placeholder,
		},
	},
	// inquiryDate
	{
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.rangePicker,
		searchOperator: OPERATORS.between,
		isSearchable: true,
		operators: [OPERATORS.between],
		field: dataKeys.inquiryDate,
		display_name: ELIGIBILITY_LIST_VIEW_FIELDS.inquiryDate.label,
		custom_props: {
			placeholder: ELIGIBILITY_LIST_VIEW_FIELDS.inquiryDate.placeholder,
		},
	},
	// serviceDates
	SERVICE_DATE_FILTER_SCHEMA,
	// provider
	{
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.multiselect,
		searchOperator: OPERATORS.multiselectContains,
		isSearchable: true,
		field: dataKeys.providerId,
		options: metadata?.previousRequestProviderGroupOptions || [],
		display_name: ELIGIBILITY_LIST_VIEW_FIELDS.provider.label,
		custom_props: {
			placeholder: ELIGIBILITY_LIST_VIEW_FIELDS.provider.placeholder,
		},
	},
	// payer
	{
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.multiselect,
		searchOperator: OPERATORS.multiselectContains,
		isSearchable: true,
		field: dataKeys.payerId,
		options: metadata?.tenantPayerOptions || [],
		display_name: ELIGIBILITY_LIST_VIEW_FIELDS.payer.label,
		custom_props: {
			placeholder: ELIGIBILITY_LIST_VIEW_FIELDS.payer.placeholder,
		},
	},
	// coverageStatus
	ALL_TAB_STATUS_FILTER_SCHEMA,
	// locationId
	{
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.multiselect,
		searchOperator: OPERATORS.multiselectContains,
		isSearchable: true,
		field: dataKeys.locationId,
		options: metadata?.previousRequestLocationGroupOptions || [],
		display_name: ELIGIBILITY_LIST_VIEW_FIELDS.searchLocation.label,
		custom_props: {
			placeholder: ELIGIBILITY_LIST_VIEW_FIELDS.searchLocation.placeholder,
		},
	},
	{
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.multiselect,
		searchOperator: OPERATORS.multiselectContains,
		isSearchable: true,
		field: dataKeys.insuranceType,
		options: INSURANCE_TYPE_MULTI_SELECT_OPTIONS,
		display_name: ELIGIBILITY_LIST_VIEW_FIELDS.insuranceType.label,
		custom_props: {
			placeholder: ELIGIBILITY_LIST_VIEW_FIELDS.insuranceType.placeholder,
		},
	},
	{
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.select,
		searchOperator: OPERATORS.equal,
		field: dataKeys.hasAdditionalPayers,
		operators: [OPERATORS.equal],
		options: ADDITIONAL_PAYER_SELECT_OPTIONS,
		display_name: ELIGIBILITY_LIST_VIEW_FIELDS.additionalPayer.label,
		custom_props: {
			placeholder: ELIGIBILITY_LIST_VIEW_FIELDS.additionalPayer.placeholder,
		},
	},
];

/**
 * Search component for eligibility responses
 */
const EligibilityFilter: React.FC<IProps> = ({ activeTab }) => {
	const isAllTab = activeTab === ELIGIBILITY_REQUESTS_TABS.values.all;

	const { searchParams: allTabSearchParams } = useEligibilityStoreValues();
	const { setSearchParams: allTabSetSearchParams, setApiParams: allTabSetApiParams } = useEligibilityStoreActions();

	const { searchParams: batchSearchParams } = useBatchUploadStoreValues();
	const { setSearchParams: batchSetSearchParams, setApiParams: batchSetApiParams } = useBatchUploadStoreActions();
	const metadata = useMetaData();
	const users = useKeycloakUsers({}).data?.data;

	const userOptions = useMemo(() => {
		return _.map(users, (user) => ({ label: user.email, value: user.id }));
	}, [users]);

	const allTabFilterSchema = useMemo(() => getAllTabFilters(metadata), [metadata]);

	const batchTabFilterSchema = [
		// status
		BATCH_STATUS_FILTER_SCHEMA,
		// createdBy
		{
			ui_field_type: QUERY_BUILDER_FILTER_TYPE.multiselect,
			searchOperator: OPERATORS.multiselectContains,
			isSearchable: true,
			field: filterDataKeys.createdBy,
			options: userOptions,
			display_name: ELIGIBILITY_BATCH_LIST_VIEW_FIELDS.createdBy.label,
			custom_props: {
				placeholder: ELIGIBILITY_BATCH_LIST_VIEW_FIELDS.createdBy.placeholder,
			},
		},
		// creationDate
		// TODO : convertDateRangeToTimeZone & input date format
		{
			ui_field_type: QUERY_BUILDER_FILTER_TYPE.datePicker,
			searchOperator: OPERATORS.equal,
			isSearchable: true,
			field: filterDataKeys.creationDate,
			display_name: ELIGIBILITY_BATCH_LIST_VIEW_FIELDS.creationDate.label,
			custom_props: {
				placeholder: ELIGIBILITY_BATCH_LIST_VIEW_FIELDS.creationDate.placeholder,
			},
		},
	];

	return (
		<AdvancedFilters
			hideFilterButton={!isAllTab}
			shouldResetInitialValue={!isAllTab}
			initialValue={isAllTab ? allTabSearchParams : batchSearchParams}
			filterSchema={isAllTab ? allTabFilterSchema : batchTabFilterSchema}
			onSubmit={(newParams) => {
				if (isAllTab) {
					allTabSetSearchParams(newParams);
					allTabSetApiParams({ criteria: newParams.filterParams });
				} else {
					batchSetSearchParams(newParams);
					batchSetApiParams({ criteria: newParams.filterParams });
				}
			}}
		/>
	);
};

export default EligibilityFilter;
export { getFilterParamsWithServiceDate, getStatusQueryParams, IN_PROGRESS_QUERY_PARAMS, getAllTabFilters };
